import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { ChevronDown, Search, X } from "lucide-react";

export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
  image?: string;
}

export interface SearchableSelectProps {
  options?: SelectOption[];
  value?: SelectOption | SelectOption[] | null;
  onChange?: (value: SelectOption | SelectOption[] | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  selectClassName?: string;
  disabled?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  maxHeight?: string;
  noOptionsMessage?: string;
  hideSelectedOptions?: boolean;
  onSearchChange?: (value: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoadingOptions?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className = "",
  selectClassName = "",
  disabled = false,
  multiple = false,
  clearable = true,
  maxHeight = "170px",
  noOptionsMessage = "No options found",
  hideSelectedOptions = false,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  isLoadingOptions = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const isSelected = useCallback(
    (option: SelectOption) => {
      if (multiple) {
        return (
          Array.isArray(value) && value.some((v) => v.value === option.value)
        );
      }

      return !Array.isArray(value) && value?.value === option.value;
    },
    [multiple, value],
  );

  // =========================
  // FILTERED OPTIONS
  // =========================
  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      if (hideSelectedOptions && multiple && isSelected(option)) {
        return false;
      }

      return option.label?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [hideSelectedOptions, isSelected, multiple, options, searchTerm]);

  // =========================
  // OUTSIDE CLICK
  // =========================
  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, []);

  // =========================
  // HELPERS
  // =========================
  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm("");
    onSearchChange?.("");
    setFocusedIndex(-1);
  };

  const updateSearchTerm = (nextValue: string) => {
    setSearchTerm(nextValue);
    setFocusedIndex(0);
    onSearchChange?.(nextValue);
    setIsOpen(true);
  };

  // =========================
  // SELECT OPTION
  // =========================
  const handleOptionSelect = (option: SelectOption) => {
    if (option.disabled) return;

    if (multiple) {
      const current = Array.isArray(value) ? value : [];

      if (isSelected(option)) {
        setSearchTerm("");
        onSearchChange?.("");
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        return;
      }

      onChange?.([...current, option]);
      setSearchTerm("");
      onSearchChange?.("");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return;
    }

    onChange?.(option);
    closeDropdown();
  };

  // =========================
  // CLEAR
  // =========================
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange?.(multiple ? [] : null);
  };

  // =========================
  // REMOVE TAG
  // =========================
  const handleRemoveTag = (
    option: SelectOption,
    event?: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!multiple) return;

    if (!Array.isArray(value)) return;

    onChange?.(value.filter((v) => v.value !== option.value));
  };

  // =========================
  // KEYBOARD NAVIGATION
  // =========================
  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, filteredOptions.length);
    if (!isOpen) return;

    if (filteredOptions.length === 0) {
      setFocusedIndex(-1);
      return;
    }

    setFocusedIndex((current) =>
      current < 0 || current >= filteredOptions.length ? 0 : current,
    );
  }, [filteredOptions.length, isOpen]);

  useEffect(() => {
    if (!isOpen || focusedIndex < 0) return;
    optionRefs.current[focusedIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [focusedIndex, isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((prev) =>
          filteredOptions.length
            ? prev < filteredOptions.length - 1
              ? prev + 1
              : 0
            : -1,
        );
        break;

      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((prev) =>
          filteredOptions.length
            ? prev > 0
              ? prev - 1
              : filteredOptions.length - 1
            : -1,
        );
        break;

      case "Enter":
        event.preventDefault();
        if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          handleOptionSelect(filteredOptions[focusedIndex]);
        }
        break;

      case "Escape":
        event.preventDefault();
        closeDropdown();
        break;
    }
  };

  // =========================
  // TOGGLE
  // =========================
  const toggleDropdown = () => {
    if (disabled) return;

    setIsOpen((prev) => !prev);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : !!value;

  const handleOptionsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoadingMore || isLoadingOptions) return;

    const target = event.currentTarget;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= 24) {
      onLoadMore?.();
    }
  };

  const renderLoadingMore = () =>
    (isLoadingMore || isLoadingOptions) && (
      <div className="p-3 text-center text-sm text-gray-500">
        {isLoadingMore ? "Loading more..." : "Loading..."}
      </div>
    );

  if (multiple) {
    const selectedOptions = Array.isArray(value) ? value : [];

    return (
      <div
        ref={dropdownRef}
        onKeyDown={handleKeyDown}
        className={`relative w-full ${className}`}
      >
        <div
          className={`
            ${selectClassName}
            overflow-hidden
            rounded-lg
            border
            border-slate-200
            bg-white
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            ${isOpen ? "border-blue-400 ring-2 ring-blue-100" : ""}
          `}
        >
          <div
            className={`flex min-h-[42px] flex-wrap items-center gap-2 px-3 py-2 ${
              selectedOptions.length ? "border-b border-slate-100" : ""
            }`}
          >
            {selectedOptions.length ? (
              selectedOptions.map((item) => (
                <span
                  key={item.value}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700"
                >
                  <span className="truncate">{item.label}</span>
                  <button
                    disabled={disabled}
                    type="button"
                    aria-label={`Remove ${item.label}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => handleRemoveTag(item, event)}
                    className="inline-flex h-4 w-4 items-center justify-center text-blue-300 transition hover:text-blue-700"
                  >
                    <X size={13} />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-sm text-slate-400">{placeholder}</span>
            )}
          </div>

          <div className="bg-slate-50 px-3 py-2">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                disabled={disabled}
                onFocus={() => setIsOpen(true)}
                onChange={(event) => {
                  updateSearchTerm(event.target.value);
                }}
                placeholder={searchPlaceholder}
                className="h-11 w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 outline-none transition focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {isOpen && !disabled && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
            <div
              ref={listRef}
              role="listbox"
              className="overflow-y-auto"
              style={{ maxHeight }}
              onScroll={handleOptionsScroll}
            >
              {filteredOptions.length === 0 && !isLoadingOptions ? (
                <div className="p-3 text-center text-gray-500">
                  {noOptionsMessage}
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <button
                    key={option.value}
                    ref={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    type="button"
                    role="option"
                    aria-selected={focusedIndex === index || isSelected(option)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleOptionSelect(option)}
                    disabled={option.disabled}
                    className={`
                      flex
                      w-full
                      items-center
                      justify-between
                      px-3
                      py-2
                      text-left
                      hover:bg-slate-50
                      ${focusedIndex === index ? "bg-blue-50" : ""}
                      ${isSelected(option) ? "bg-blue-50 text-blue-700" : ""}
                    `}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {option.image && (
                        <img
                          src={option.image}
                          alt={option.label}
                          className="h-5 w-5 rounded"
                        />
                      )}
                      <span className="truncate">{option.label}</span>
                    </div>

                    {isSelected(option) && (
                      <div className="h-2 w-2 rounded-full bg-blue-600" />
                    )}
                  </button>
                ))
              )}
              {renderLoadingMore()}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
      className={`relative w-full ${className}`}
    >
      {/* SELECT */}
      <div
        className={`
          ${selectClassName}
          min-h-[42px]
          border
          border-gray-300
          rounded-lg
          flex
          items-center
          justify-between
          bg-white
          w-full
          ${
            disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400"
          }
          ${isOpen ? "border-blue-500 ring-2 ring-blue-200" : ""}
        `}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={toggleDropdown}
          className="flex min-h-[42px] flex-1 items-center px-3 py-2 text-left outline-none"
        >
          <span className="flex flex-1 flex-wrap gap-1">
            <span className={value ? "" : "text-gray-400"}>
              {!Array.isArray(value) && value ? value.label : placeholder}
            </span>
          </span>
        </button>

        <div className="flex items-center gap-2 pr-3">
          {clearable && hasValue && (
            <button disabled={disabled} type="button" onClick={handleClear}>
              <X size={16} />
            </button>
          )}

          <button type="button" disabled={disabled} onClick={toggleDropdown}>
            <ChevronDown size={16} className={isOpen ? "rotate-180" : ""} />
          </button>
        </div>
      </div>

      {/* DROPDOWN */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* SEARCH */}
          <div className="p-2 border-b border-gray-300">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-3 text-gray-400"
              />

              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(event) => {
                  updateSearchTerm(event.target.value);
                }}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* OPTIONS */}
          <div
            ref={listRef}
            role="listbox"
            className="overflow-y-auto"
            style={{ maxHeight }}
            onScroll={handleOptionsScroll}
          >
            {filteredOptions.length === 0 && !isLoadingOptions ? (
              <div className="p-3 text-center text-gray-500">
                {noOptionsMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  type="button"
                  role="option"
                  aria-selected={focusedIndex === index || isSelected(option)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleOptionSelect(option)}
                  disabled={option.disabled}
                  className={`
                    w-full
                    text-left
                    px-3
                    py-2
                    flex
                    items-center
                    justify-between
                    hover:bg-gray-100
                    ${focusedIndex === index ? "bg-blue-50" : ""}
                    ${isSelected(option) ? "bg-blue-100" : ""}
                  `}
                >
                  <div className="flex items-center gap-2">
                    {option.image && (
                      <img
                        src={option.image}
                        alt={option.label}
                        className="w-5 h-5 rounded"
                      />
                    )}

                    <span>{option.label}</span>
                  </div>

                  {isSelected(option) && (
                    <div className="w-2 h-2 rounded-full bg-blue-600" />
                  )}
                </button>
              ))
            )}
            {renderLoadingMore()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
